import { AbstractConnector } from '@web3-react/abstract-connector'
import { UnsupportedChainIdError } from '@web3-react/core'
import { darken, lighten } from 'polished'
import React, { useMemo } from 'react'
import { Activity } from 'react-feather'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import CoinbaseWalletIcon from '../../assets/images/coinbaseWalletIcon.svg'
import FortmaticIcon from '../../assets/images/fortmaticIcon.png'
import PortisIcon from '../../assets/images/portisIcon.png'
import WalletConnectIcon from '../../assets/images/walletConnectIcon.svg'
import { bitget, fortmatic, injected, portis, walletconnect, walletlink } from '../../connectors'
import useENSName from '../../hooks/useENSName'
import { useHasSocks } from '../../hooks/useSocksBalance'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'
import bitgetWallet from 'assets/images/bitget.png'
import Identicon from '../Identicon'
import Loader from '../Loader'

import { RowBetween } from '../Row'
import WalletModal from '../WalletModal'
import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { useActiveWeb3React } from 'hooks'
import { SUPPORTED_WALLETS, WalletType } from '../../constants'
import { EIP6963Connector } from '../../connectors/EIP6963Connector'

const IconWrapper = styled.div<{ size?: number }>`
  ${({ theme }) => theme.flexColumnNoWrap};
  align-items: center;
  justify-content: center;
  & > * {
    height: ${({ size }) => (size ? size + 'px' : '32px')};
    width: ${({ size }) => (size ? size + 'px' : '32px')};
  }
`

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  background-color: ${({ theme }) => theme.primary1};
  border: 1px solid #4afb39;
  color: #090524;
  font-weight: 500;
  border-radius: 10px;

  :hover,
  :focus {
    border: 1px solid #4afb38;
    color: #090524;
  }
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  background-color: ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  border: 1px solid ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.text1)};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ pending, theme }) => (pending ? darken(0.05, theme.primary1) : lighten(0.05, theme.bg1))};

    :focus {
      border: 1px solid ${({ pending, theme }) => (pending ? darken(0.1, theme.primary1) : darken(0.1, theme.bg2))};
    }
  }
`

const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 14px;
  width: fit-content;
  font-weight: 400;
`

const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

// we want the latest one to come first, so return negative if a is after b
function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const SOCK = (
  <span role="img" aria-label="has socks emoji" style={{ marginTop: -4, marginBottom: -4 }}>
    🧦
  </span>
)

// eslint-disable-next-line react/prop-types
function StatusIcon({ connector }: { connector: AbstractConnector }) {
  if (connector === injected) {
    return <Identicon />
  } else if (connector === bitget) {
    return (
      <IconWrapper size={16}>
        <img src={bitgetWallet} alt={''} />
      </IconWrapper>
    )
  } else if (connector === walletconnect) {
    return (
      <IconWrapper size={16}>
        <img src={WalletConnectIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === walletlink) {
    return (
      <IconWrapper size={16}>
        <img src={CoinbaseWalletIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === fortmatic) {
    return (
      <IconWrapper size={16}>
        <img src={FortmaticIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === portis) {
    return (
      <IconWrapper size={16}>
        <img src={PortisIcon} alt={''} />
      </IconWrapper>
    )
  }
  return null
}

function Web3StatusInner() {
  const { t } = useTranslation()

  const { account, connector, error, chainId } = useActiveWeb3React()

  // CUSTOM: Do not handle ENS
  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)

  const hasPendingTransactions = !!pending.length
  const hasSocks = useHasSocks()
  const toggleWalletModal = useWalletModalToggle()

  const wrongNetwork = chainId?.toString() !== CURRENT_CHAIN.toString()

  if (!account) {
    return (
      <Web3StatusConnect id="connect-wallet" onClick={toggleWalletModal} style={{ height: '30px' }}>
        <Text>Connect Wallet</Text>
      </Web3StatusConnect>
    )
  } else if (account && !wrongNetwork) {
    return (
      <Web3StatusConnected id="web3-status-connected" onClick={toggleWalletModal} pending={hasPendingTransactions}>
        {hasPendingTransactions ? (
          <RowBetween>
            <Text style={{ color: '#090524' }}>{pending?.length} Pending</Text> <Loader stroke="#090524" />
          </RowBetween>
        ) : (
          <>
            {hasSocks ? SOCK : null}
            <Text>{ENSName ? shortenAddress(account) : shortenAddress(account, 3)}</Text>
          </>
        )}
        {!hasPendingTransactions && connector && <StatusIcon connector={connector} />}
      </Web3StatusConnected>
    )
  } else if (error || wrongNetwork) {
    return (
      <Web3StatusError onClick={toggleWalletModal}>
        <NetworkIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="#FF6871"
            stroke="#FF6871"
            strokeWidth="1"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path
              d="M3 17h2.397a5 5 0 0 0 4.096 -2.133l.177 -.253m3.66 -5.227l.177 -.254a5 5 0 0 1 4.096 -2.133h3.397"
              fill="#FF6871"
            />
            <path d="M18 4l3 3l-3 3" fill="#FF6871" />
            <path d="M3 7h2.397a5 5 0 0 1 4.096 2.133l4.014 5.734a5 5 0 0 0 4.096 2.133h3.397" fill="#FF6871" />
            <path d="M18 20l3 -3l-3 -3" fill="#FF6871" />
          </svg>
        </NetworkIcon>
        <Text>{error instanceof UnsupportedChainIdError || wrongNetwork ? 'Wrong Network' : 'Error'}</Text>
      </Web3StatusError>
    )
  } else {
    return (
      <Web3StatusConnect id="connect-wallet" onClick={toggleWalletModal}>
        <Text>{t('Connect to a wallet')}</Text>
      </Web3StatusConnect>
    )
  }
}

function connectWithEIP6963() {
  if (typeof window.ethereum === 'undefined') {
    console.error('No EIP-1193 wallet detected')
    return
  }

  const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    return (
      navigator.maxTouchPoints > 0 && /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    )
  }

  // For now, handleEIP6963Announce is implemented directly here,
  // but it needs to be moved to a component with useEffect later on.
  function handleEIP6963Announce(event: any) {
    const { info, provider } = event.detail

    // RDNS on brave mobile is the same as the desktop one
    const isMobileWallet =
      info.rdns === 'io.metamask.mobile' ||
      info.rdns === 'com.debank.rabbymobile' ||
      info.rdns === 'com.bitget.web3' ||
      (info.rdns === 'com.brave.wallet' && isMobileDevice())

    // On mobile, only one wallet per application
    if (isMobileWallet) {
      // Keep only the relevant wallet in SUPPORTED_WALLETS
      Object.keys(SUPPORTED_WALLETS).forEach((key) => {
        if (
          (info.rdns === 'io.metamask.mobile' && key !== WalletType.METAMASK) ||
          (info.rdns === 'com.debank.rabbymobile' && key !== WalletType.RABBY) ||
          (info.rdns === 'com.brave.wallet' && key !== WalletType.INJECTED) ||
          (info.rdns === 'com.bitget.web3' && key !== WalletType.BITGET)
        ) {
          delete SUPPORTED_WALLETS[key]
        }
      })
    }

    if (info.rdns === 'io.metamask' || info.rdns === 'io.metamask.mobile') {
      SUPPORTED_WALLETS.METAMASK.connector = new EIP6963Connector(provider)
    }
    if (info.rdns === 'io.rabby' || info.rdns === 'com.debank.rabbymobile') {
      SUPPORTED_WALLETS.RABBY.connector = new EIP6963Connector(provider)
    }
    if (info.rdns === 'com.brave.wallet') {
      SUPPORTED_WALLETS.INJECTED.connector = new EIP6963Connector(provider)
    }
  }

  // Add event listener
  window.addEventListener('eip6963:announceProvider', handleEIP6963Announce)

  // Request wallet announcement
  window.dispatchEvent(new Event('eip6963:requestProvider'))
}

export default function Web3Status() {
  const { account } = useActiveWeb3React()

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)
  const confirmed = sortedRecentTransactions.filter((tx) => tx.receipt).map((tx) => tx.hash)

  connectWithEIP6963()
  /*  if (!contextNetwork.active && !active) {
    return null
  } */

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
